import { Slider } from "@base-ui/react/slider";
import { type FC } from "react";

import { PageEventType } from "components/server-types";
import { Tooltip } from "mds/components/Tooltip";
import { cn } from "mds/utils/cn";
import { EventMarkerTooltip } from "worksheets/views/timeline/EventMarkerTooltip";

export type VersionHistoryScrubberMark = {
  index: number;
  dateLabel: string;
  eventType?: PageEventType.PUBLICATION | PageEventType.SUBMISSION_COLLECTION;
};

const markClassName = (index: number, value: number) => {
  if (index === value) {
    return "border-blue bg-blue";
  }

  if (index < value) {
    return "border-blue bg-white";
  }

  return "border-blue-tint-70 bg-white";
};

type VersionHistoryScrubberProps = {
  count: number;
  value: number;
  valueText: string;
  labelledBy: string;
  describedBy: string;
  marks: VersionHistoryScrubberMark[];
  onChange: (index: number) => void;
};

// TODO: Look into making an MDS component for the scrubber.
export const VersionHistoryScrubber: FC<VersionHistoryScrubberProps> = ({
  count,
  value,
  valueText,
  labelledBy,
  describedBy,
  marks,
  onChange,
}) => {
  // Base UI requires min !== max; clamp commits so a single snapshot cannot seek to 1.
  const max = Math.max(count - 1, 1);

  return (
    <Slider.Root
      aria-labelledby={labelledBy}
      className="w-full"
      disabled={count <= 1}
      max={max}
      min={0}
      step={1}
      value={value}
      onValueChange={(next) => onChange(Math.min(next, count - 1))}
    >
      <Slider.Control className="relative flex h-6 w-full touch-none select-none items-center">
        <Slider.Track className="relative h-1 w-full overflow-visible bg-blue-tint-70">
          <Slider.Indicator className="h-full bg-blue" />

          {marks.map((mark) => {
            const markNode = (
              <div
                className={cn("size-3 rounded-full border-2", markClassName(mark.index, value))}
                data-timeline-mark={mark.index}
              />
            );

            return (
              <div
                className="absolute top-1/2 z-10 size-3 -translate-x-1/2 -translate-y-1/2"
                key={mark.index}
                style={{ left: `${(mark.index / max) * 100}%` }}
                aria-hidden
              >
                {mark.eventType ? (
                  <Tooltip
                    content={
                      <EventMarkerTooltip dateLabel={mark.dateLabel} eventType={mark.eventType} />
                    }
                    contentClassName="!max-w-none"
                    kind="card"
                    place="bottom"
                    positionStrategy="fixed"
                    wrapperClassName="flex size-3 items-center justify-center"
                  >
                    {markNode}
                  </Tooltip>
                ) : (
                  markNode
                )}
              </div>
            );
          })}

          {/* Thumb is required for drag/keyboard; hide it so only event marks show a filled dot. */}
          <Slider.Thumb
            aria-describedby={describedBy}
            aria-labelledby={labelledBy}
            aria-valuetext={valueText}
            className="size-3 rounded-full opacity-0 outline-none"
          />
        </Slider.Track>
      </Slider.Control>
    </Slider.Root>
  );
};
